import {
  AhaEditor,
  type AhaEditorV2Controller,
  type EditorPlugin,
  type slate,
} from '@aha-app/aha-editor-v2';
import ToolInsertReport from './tools/ToolInsertReport';

// WARNING: Ensure packages/aha-editor-v2/src/serialization/htmlSerializers/SerializerEmbeddedReport.tsx is updated if you update this regex.
export const URLRegex =
  /bookmarks\/(?:(custom_pivots|feature_grids|idea_grids|gantt_roadmaps|epic_gantt_roadmaps|initiative_gantt_roadmaps)\/)?(\d+)/;

function getReportIdFromURL(textURL: string): RecordId | null {
  return textURL.match(URLRegex)?.[2] as RecordId;
}

function isEmbeddedReportURL(textURL: string): boolean {
  // Check if the text does not contain whitespaces in the middle, which would indicate a text to paste instead of a report URL.
  if (textURL.trim().split(/\s+/).length !== 1) {
    return false;
  }

  return URLRegex.test(textURL) && Boolean(getReportIdFromURL(textURL));
}

function reportNameFromURL(textURL) {
  const type = textURL.match(URLRegex)[1];
  switch (type) {
    case 'custom_pivots':
      return 'Custom report';
    case 'feature_grids':
      return 'Features';
    case 'idea_grids':
      return 'Ideas';
    default:
      return 'Report';
  }
}

function insertEmbeddedReport(
  editor: slate.Editor,
  controller: AhaEditorV2Controller,
  url: string,
  name: string
): void {
  const id = getReportIdFromURL(url);

  if (!id) return;

  ToolInsertReport.insertReport({
    editor,
    controller,
    payload: { bookmarkId: id, name },
  });
}

function findEmbeddedReportsInHTML(
  html: string
): Array<{ name: string; href: string }> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  return Array.from(doc.body.querySelectorAll(':scope a'))
    .map((anchor: HTMLLinkElement) => {
      const href = anchor.href;
      const name = anchor.innerText;
      return { name, href };
    })
    .filter(anchor => isEmbeddedReportURL(anchor.href));
}

interface EmbeddedReportPastePluginOptions {
  advancedPasteEnabled: boolean;
}

const EmbeddedReportPastePlugin: (
  options: EmbeddedReportPastePluginOptions
) => EditorPlugin = ({ advancedPasteEnabled }) => ({
  name: 'Embedded report Paste Plugin',

  insertData: (
    editor: slate.Editor,
    data: DataTransfer,
    controller: AhaEditorV2Controller
  ) => {
    if (!advancedPasteEnabled) {
      return false;
    }

    // Skip if data is from our own editor clipboard format
    if (data.types.includes(`application/${AhaEditor.clipboardFormatKey}`)) {
      return false;
    }

    // Handle HTML pastes (e.g., from copying links)
    if (data.types.includes('text/html')) {
      const html = data.getData('text/html');
      const embeddedReports = findEmbeddedReportsInHTML(html);

      if (embeddedReports && embeddedReports.length > 0) {
        embeddedReports.forEach(report => {
          insertEmbeddedReport(editor, controller, report.href, report.name);
        });
        return true;
      }
    }

    // Handle plain text pastes
    if (data.types.includes('text/plain')) {
      const text = data.getData('text/plain');

      if (isEmbeddedReportURL(text)) {
        insertEmbeddedReport(editor, controller, text, reportNameFromURL(text));
        return true;
      }
    }

    return false;
  },
});

export default EmbeddedReportPastePlugin;
